import React, { useState, useEffect } from "react";
import { X, Calendar, Clock } from "lucide-react";
import moment from "moment-timezone";
import { updateCampaignSchedule } from "../apis/campaign";
import { toast } from "react-toastify";

/**
 * Update Schedule Modal
 * Allows user to update the scheduled time for a scheduled campaign
 */
const UpdateScheduleModal = ({ isOpen, onClose, campaign, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    scheduledDate: "",
    scheduledTime: "",
    timezone: "Asia/Kolkata",
  });

  // Common timezones
  const timezones = [
    { value: "Asia/Kolkata", label: "Asia/Kolkata (IST)" },
    { value: "America/New_York", label: "America/New_York (EST)" },
    { value: "America/Los_Angeles", label: "America/Los_Angeles (PST)" },
    { value: "Europe/London", label: "Europe/London (GMT)" },
    { value: "Europe/Paris", label: "Europe/Paris (CET)" },
    { value: "Asia/Dubai", label: "Asia/Dubai (GST)" },
    { value: "Asia/Singapore", label: "Asia/Singapore (SGT)" },
    { value: "Australia/Sydney", label: "Australia/Sydney (AEDT)" },
  ];

  useEffect(() => {
    if (isOpen && campaign) {
      // Parse existing scheduled time if available
      if (campaign.scheduled_time_original) {
        const [datePart, timePart] = campaign.scheduled_time_original.split(" ");
        setFormData({
          scheduledDate: datePart || "",
          scheduledTime: timePart ? timePart.substring(0, 5) : "",
          timezone: campaign.timezone || "Asia/Kolkata",
        });
      } else if (campaign.scheduled_at) {
        const scheduledDate = new Date(campaign.scheduled_at);
        const dateStr = scheduledDate.toISOString().split("T")[0];
        const timeStr = scheduledDate.toTimeString().substring(0, 5);
        setFormData({
          scheduledDate: dateStr,
          scheduledTime: timeStr,
          timezone: campaign.timezone || "Asia/Kolkata",
        });
      }
    }
  }, [isOpen, campaign]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.scheduledDate || !formData.scheduledTime) {
      toast.error("Please select both date and time");
      return;
    }

    // Combine date and time into YYYY-MM-DD HH:MM:SS format
    const scheduledTimestamp = `${formData.scheduledDate} ${formData.scheduledTime}:00`;

    // Validate that the scheduled time is in the future using the selected timezone
    const scheduledMoment = moment.tz(
      `${formData.scheduledDate} ${formData.scheduledTime}`,
      "YYYY-MM-DD HH:mm",
      formData.timezone
    );

    if (!scheduledMoment.isValid()) {
      toast.error("Invalid date or time values");
      return;
    }

    // Get current time in the selected timezone
    const nowInTimezone = moment.tz(formData.timezone);

    // Compare in the same timezone
    if (scheduledMoment.isSameOrBefore(nowInTimezone)) {
      toast.error("Scheduled time must be in the future. Please select a future date and time.");
      return;
    }

    try {
      setLoading(true);
      
      // Validate campaign ID
      if (!campaign?.campaign_id) {
        toast.error("Invalid campaign ID");
        console.error("[UpdateSchedule] Invalid campaign_id:", campaign);
        return;
      }

      // API function doesn't need token - it's handled by interceptor
      const response = await updateCampaignSchedule(
        campaign.campaign_id,
        scheduledTimestamp,
        formData.timezone
      );

      if (response.data.success) {
        toast.success(response.data.message || "Campaign schedule updated successfully");
        onSuccess?.();
        onClose();
      } else {
        toast.error(response.data.message || "Failed to update campaign schedule");
      }
    } catch (error) {
      console.error("Error updating campaign schedule:", error);
      const errorMessage = error.response?.data?.error || "Failed to update campaign schedule";
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Get minimum date (today)
  const today = new Date().toISOString().split("T")[0];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <Calendar className="w-5 h-5 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900">Update Schedule</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-4">
          <div className="mb-4">
            <p className="text-sm text-gray-700 mb-4">
              Update the scheduled time for <strong>{campaign.campaign_name}</strong>.
            </p>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
              <p className="text-xs text-blue-900">
                <strong>Note:</strong> The scheduled time must be in the future. Please ensure you
                select a valid date and time.
              </p>
            </div>
          </div>

          {/* Date Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scheduled Date <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              name="scheduledDate"
              value={formData.scheduledDate}
              onChange={handleChange}
              min={today}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>

          {/* Time Input */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scheduled Time <span className="text-red-500">*</span>
            </label>
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4 text-gray-500" />
              <input
                type="time"
                name="scheduledTime"
                value={formData.scheduledTime}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Format: HH:MM (24-hour format)</p>
          </div>

          {/* Timezone Select */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Timezone <span className="text-red-500">*</span>
            </label>
            <select
              name="timezone"
              value={formData.timezone}
              onChange={handleChange}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              {timezones.map((tz) => (
                <option key={tz.value} value={tz.value}>
                  {tz.label}
                </option>
              ))}
            </select>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-2 mt-4">
            <button
              type="button"
              onClick={onClose}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  Updating...
                </>
              ) : (
                "Update Schedule"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default UpdateScheduleModal;

